import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api, money, toPaise, fromPaise, today, addDays, ApiError, DOC_LABELS } from '../api'
import { Page } from '../App'
import { Card, Alert, Spinner, Field } from '../components/ui'
import type { Client } from './Clients'

interface Item {
  id: string
  name: string
  description: string | null
  sac_hsn: string | null
  unit: string
  rate: number
  tax_rate: number
}

interface LineDraft {
  key: string
  description: string
  sac_hsn: string
  qty: string
  unit: string
  rate: string
  discount_mode: 'pct' | 'amount'
  discount_pct: string
  discount_flat: string
  tax_rate: string
  item_id: string | null
}

interface AdjustmentDraft {
  key: string
  label: string
  amount: string
}

interface Preview {
  treatment: string
  treatmentLabel: string
  posStateCode: string | null
  subtotal: number
  discountTotal: number
  lineDiscountTotal: number
  invoiceDiscountTotal: number
  adjustmentTotal: number
  taxableValue: number
  cgst: number
  sgst: number
  igst: number
  cess: number
  roundOff: number
  total: number
  warnings: string[]
}

const blankLine = (): LineDraft => ({
  key: Math.random().toString(36).slice(2),
  description: '',
  sac_hsn: '',
  qty: '1',
  unit: 'NOS',
  rate: '',
  discount_mode: 'pct',
  discount_pct: '0',
  discount_flat: '',
  tax_rate: '18',
  item_id: null,
})

export default function InvoiceEdit() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()

  const [clients, setClients] = useState<Client[]>([])
  const [items, setItems] = useState<Item[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)
  const [previewError, setPreviewError] = useState('')
  const [nextNumber, setNextNumber] = useState<string>('')

  const [docType, setDocType] = useState('tax_invoice')
  const [clientId, setClientId] = useState(params.get('client') ?? '')
  const [issueDate, setIssueDate] = useState(today())
  const [dueDate, setDueDate] = useState('')
  const [poNumber, setPoNumber] = useState('')
  const [notes, setNotes] = useState('')
  const [terms, setTerms] = useState('')
  const [currency, setCurrency] = useState('INR')
  const [fxRate, setFxRate] = useState('1')
  const [reverseCharge, setReverseCharge] = useState(false)
  const [discountMode, setDiscountMode] = useState<'none' | 'pct' | 'amount'>('none')
  const [discountValue, setDiscountValue] = useState('')
  const [adjustments, setAdjustments] = useState<AdjustmentDraft[]>([])
  const [lines, setLines] = useState<LineDraft[]>([blankLine()])

  const client = useMemo(() => clients.find((c) => c.id === clientId), [clients, clientId])

  // ------------------------------------------------------------------- load
  useEffect(() => {
    ;(async () => {
      try {
        const [cRes, iRes] = await Promise.all([
          api.get<{ clients: Client[] }>('/clients'),
          api.get<{ items: Item[] }>('/items'),
        ])
        setClients(cRes.clients)
        setItems(iRes.items)

        if (id) {
          const inv = await api.get<{
            invoice: Record<string, never>
            lines: Record<string, never>[]
            editable: boolean
          }>(`/invoices/${id}`)
          const v = inv.invoice as unknown as Record<string, string | number | null>
          if (!inv.editable) {
            navigate(`/invoices/${id}`, { replace: true })
            return
          }
          setDocType(String(v.doc_type))
          setClientId(String(v.client_id))
          setIssueDate(String(v.issue_date))
          setDueDate(String(v.due_date))
          setPoNumber(String(v.po_number ?? ''))
          setNotes(String(v.notes ?? ''))
          setTerms(String(v.terms ?? ''))
          setCurrency(String(v.currency))
          setFxRate(String(v.fx_rate))
          setReverseCharge(v.reverse_charge === 1)
          const dm = String(v.discount_mode ?? 'none') as 'none' | 'pct' | 'amount'
          setDiscountMode(dm)
          setDiscountValue(
            dm === 'none' ? '' : dm === 'pct' ? String(v.discount_value) : fromPaise(Number(v.discount_value)),
          )
          setAdjustments(
            ((inv as unknown as { adjustments?: Record<string, string | number>[] }).adjustments ?? []).map(
              (a) => ({
                key: Math.random().toString(36).slice(2),
                label: String(a.label),
                amount: fromPaise(Number(a.amount)),
              }),
            ),
          )
          setLines(
            (inv.lines as unknown as Record<string, string | number | null>[]).map((l) => ({
              key: Math.random().toString(36).slice(2),
              description: String(l.description),
              sac_hsn: String(l.sac_hsn ?? ''),
              qty: String(l.qty),
              unit: String(l.unit),
              rate: fromPaise(Number(l.rate)),
              discount_mode: (String(l.discount_mode) === 'amount' ? 'amount' : 'pct') as 'pct' | 'amount',
              discount_pct: String(l.discount_pct),
              discount_flat: Number(l.discount_flat) ? fromPaise(Number(l.discount_flat)) : '',
              tax_rate: String(l.tax_rate),
              item_id: l.item_id ? String(l.item_id) : null,
            })),
          )
        }
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Failed to load')
      } finally {
        setLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Peek at the number this will get, without consuming it.
  useEffect(() => {
    api
      .get<{ number: string }>(`/invoices/next-number?doc_type=${docType}&date=${issueDate}`)
      .then((r) => setNextNumber(r.number))
      .catch(() => setNextNumber(''))
  }, [docType, issueDate])

  // Applying client defaults on selection.
  useEffect(() => {
    if (!client) return
    setCurrency(client.currency)
    setDueDate((d) => d || addDays(issueDate, client.payment_terms_days))
    setLines((ls) =>
      ls.map((l) =>
        l.description || l.rate
          ? l
          : { ...l, sac_hsn: client.default_sac ?? '', tax_rate: String(client.default_tax_rate) },
      ),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId])

  useEffect(() => {
    if (client && issueDate) setDueDate(addDays(issueDate, client.payment_terms_days))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueDate])

  // Live totals from the server engine — the UI never computes tax itself, so
  // what you see is exactly what will be saved.
  useEffect(() => {
    if (!clientId) return setPreview(null)
    const payload = {
      client_id: clientId,
      doc_type: docType,
      currency,
      lines: lines
        .filter((l) => l.description.trim())
        .map((l) => ({
          description: l.description,
          sac_hsn: l.sac_hsn || null,
          qty: Number(l.qty) || 0,
          unit: l.unit,
          rate_paise: toPaise(l.rate || '0'),
          discount_mode: l.discount_mode,
          discount_pct: Number(l.discount_pct) || 0,
          discount_flat_paise: toPaise(l.discount_flat || '0'),
          tax_rate: Number(l.tax_rate) || 0,
        })),
      adjustments: adjustments
        .filter((a) => a.label.trim() && Number(a.amount))
        .map((a) => ({ label: a.label, amount_paise: toPaise(a.amount) })),
      discount_mode: discountMode,
      ...(discountMode === 'pct'
        ? { discount_value: Number(discountValue) || 0 }
        : discountMode === 'amount'
          ? { discount_value_paise: toPaise(discountValue || '0') }
          : {}),
    }
    if (payload.lines.length === 0) return setPreview(null)

    const t = setTimeout(() => {
      api
        .post<Preview>('/invoices/preview', payload)
        .then((p) => {
          setPreview(p)
          setPreviewError('')
        })
        .catch((e) => {
          // Never fail silently here. Swallowing this left the totals sitting at
          // zero with no explanation, which reads as "the app is broken" rather
          // than "line 3 was rejected" — the user cannot act on a blank number.
          setPreview(null)
          setPreviewError(e instanceof ApiError ? e.message : 'Could not calculate totals')
        })
    }, 220)
    return () => clearTimeout(t)
  }, [clientId, docType, currency, lines, discountMode, discountValue, adjustments])

  // ------------------------------------------------------------------ actions
  const setLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))

  const pickItem = (key: string, itemId: string) => {
    const item = items.find((i) => i.id === itemId)
    if (!item) return setLine(key, { item_id: null })
    setLine(key, {
      item_id: item.id,
      description: item.description || item.name,
      sac_hsn: item.sac_hsn ?? '',
      unit: item.unit,
      rate: item.rate ? fromPaise(item.rate) : '',
      tax_rate: String(item.tax_rate),
    })
  }

  const body = () => ({
    doc_type: docType,
    client_id: clientId,
    issue_date: issueDate,
    due_date: dueDate || null,
    currency,
    fx_rate: Number(fxRate) || 1,
    reverse_charge: reverseCharge,
    po_number: poNumber || null,
    notes: notes || null,
    terms: terms || null,
    lines: lines
      .filter((l) => l.description.trim())
      .map((l) => ({
        description: l.description,
        sac_hsn: l.sac_hsn || null,
        qty: Number(l.qty) || 0,
        unit: l.unit,
        rate_paise: toPaise(l.rate || '0'),
        discount_pct: Number(l.discount_pct) || 0,
        tax_rate: Number(l.tax_rate) || 0,
        item_id: l.item_id,
      })),
  })

  const save = async (thenFinalize: boolean) => {
    setError('')
    if (!clientId) return setError('Choose a client first')
    if (body().lines.length === 0) return setError('Add at least one line with a description')

    setSaving(true)
    try {
      const saved = id
        ? await api.put<{ id: string }>(`/invoices/${id}`, body())
        : await api.post<{ id: string }>('/invoices', body())

      if (thenFinalize) {
        await api.post(`/invoices/${saved.id}/finalize`)
      }
      navigate(`/invoices/${saved.id}`)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Page title="Invoice"><Spinner /></Page>

  const showDiscount = lines.some(
    (l) => Number(l.discount_pct) > 0 || Number(l.discount_flat) > 0 || l.discount_mode === 'amount',
  )
  const isIntra = preview?.treatment === 'intra'
  const zeroRated = ['export_lut', 'sez_lut', 'exempt', 'nil'].includes(preview?.treatment ?? '')

  return (
    <Page
      title={id ? 'Edit draft' : `New ${DOC_LABELS[docType]?.toLowerCase() ?? 'invoice'}`}
      subtitle={
        nextNumber ? `Will be numbered ${nextNumber} when finalized` : 'Draft — no number assigned yet'
      }
      actions={
        <>
          <button className="btn" onClick={() => navigate(-1)} disabled={saving}>
            Cancel
          </button>
          <button className="btn" onClick={() => save(false)} disabled={saving}>
            Save draft
          </button>
          <button className="btn primary" onClick={() => save(true)} disabled={saving}>
            {saving ? 'Saving…' : 'Save & finalize'}
          </button>
        </>
      }
    >
      <Alert kind="error">{error}</Alert>
      <Alert kind="error">
        {previewError && (
          <>
            <strong>Totals could not be calculated.</strong> {previewError}
          </>
        )}
      </Alert>

      {preview && preview.warnings.length > 0 && (
        <Alert kind="warn">
          <strong>Check before finalizing</strong>
          <ul>
            {preview.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Card title="Details">
        <div className="grid c4">
          <Field label="Document type">
            <select value={docType} onChange={(e) => setDocType(e.target.value)} disabled={Boolean(id)}>
              {['tax_invoice', 'proforma', 'estimate', 'bill_of_supply'].map((d) => (
                <option key={d} value={d}>
                  {DOC_LABELS[d]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Client">
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Select a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name || c.legal_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Invoice date">
            <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          </Field>
          <Field label="Due date">
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
        </div>

        <div className="grid c4">
          <Field label="Currency">
            <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          {currency !== 'INR' && (
            <Field label={`Exchange rate (₹ per 1 ${currency})`} hint="RBI reference rate on the invoice date.">
              <input type="number" step="0.01" value={fxRate} onChange={(e) => setFxRate(e.target.value)} />
            </Field>
          )}
          <Field label="PO / reference">
            <input type="text" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
          </Field>
          <Field label="Tax treatment">
            <input type="text" value={preview?.treatmentLabel ?? 'Select a client'} disabled />
          </Field>
        </div>

        <label className="check">
          <input type="checkbox" checked={reverseCharge} onChange={(e) => setReverseCharge(e.target.checked)} />
          Tax payable on reverse charge basis
        </label>
      </Card>

      <div style={{ height: 14 }} />

      <Card
        title="Line items"
        actions={
          <button className="btn sm" onClick={() => setLines((ls) => [...ls, blankLine()])}>
            + Add line
          </button>
        }
      >
        <div className="tbl-wrap">
          <table className="lines">
            <thead>
              <tr>
                <th style={{ width: 150 }}>From catalog</th>
                <th>Description</th>
                <th style={{ width: 92 }}>HSN/SAC</th>
                <th style={{ width: 68 }}>Qty</th>
                <th style={{ width: 68 }}>Unit</th>
                <th style={{ width: 104 }}>Rate</th>
                {showDiscount && <th style={{ width: 128 }}>Discount</th>}
                <th style={{ width: 74 }}>GST %</th>
                <th className="r" style={{ width: 108 }}>
                  Amount
                </th>
                <th style={{ width: 32 }} />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => {
                const lineAmount = preview?.total !== undefined ? null : null
                void lineAmount
                const gross = Math.round((Number(l.qty) || 0) * toPaise(l.rate || '0'))
                const disc = Math.min(
                  gross,
                  l.discount_mode === 'amount'
                    ? toPaise(l.discount_flat || '0')
                    : Math.round((gross * (Number(l.discount_pct) || 0)) / 100),
                )
                const taxable = gross - disc
                return (
                  <tr key={l.key}>
                    <td>
                      <select value={l.item_id ?? ''} onChange={(e) => pickItem(l.key, e.target.value)}>
                        <option value="">—</option>
                        {items.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={l.description}
                        placeholder="What are you billing for?"
                        onChange={(e) => setLine(l.key, { description: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="mono"
                        value={l.sac_hsn}
                        onChange={(e) => setLine(l.key, { sac_hsn: e.target.value })}
                      />
                    </td>
                    <td>
                      <input type="number" step="0.01" value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} />
                    </td>
                    <td>
                      <input type="text" value={l.unit} onChange={(e) => setLine(l.key, { unit: e.target.value.toUpperCase() })} />
                    </td>
                    <td>
                      <input type="number" step="0.01" value={l.rate} onChange={(e) => setLine(l.key, { rate: e.target.value })} />
                    </td>
                    {showDiscount && (
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <select
                            style={{ width: 52, padding: '6px 4px' }}
                            value={l.discount_mode}
                            onChange={(e) =>
                              setLine(l.key, { discount_mode: e.target.value as 'pct' | 'amount' })
                            }
                          >
                            <option value="pct">%</option>
                            <option value="amount">₹</option>
                          </select>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={l.discount_mode === 'pct' ? l.discount_pct : l.discount_flat}
                            onChange={(e) =>
                              setLine(
                                l.key,
                                l.discount_mode === 'pct'
                                  ? { discount_pct: e.target.value }
                                  : { discount_flat: e.target.value },
                              )
                            }
                          />
                        </div>
                      </td>
                    )}
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        value={l.tax_rate}
                        disabled={zeroRated}
                        onChange={(e) => setLine(l.key, { tax_rate: e.target.value })}
                      />
                    </td>
                    <td className="amt">{money(taxable, currency)}</td>
                    <td>
                      <button
                        className="btn ghost sm"
                        title="Remove line"
                        onClick={() =>
                          setLines((ls) => (ls.length === 1 ? [blankLine()] : ls.filter((x) => x.key !== l.key)))
                        }
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {!showDiscount && (
          <button
            className="btn ghost sm"
            style={{ marginTop: 8 }}
            onClick={() => setLines((ls) => ls.map((l, i) => (i === 0 ? { ...l, discount_mode: 'amount' } : l)))}
          >
            + Add a discount column
          </button>
        )}

        <div className="sep" />

        <div className="totals-box">
          <div className="row">
            <span>Subtotal</span>
            <span className="num">{money(preview?.subtotal ?? 0, currency)}</span>
          </div>
          {Boolean(preview?.lineDiscountTotal) && (
            <div className="row">
              <span>Line discounts</span>
              <span className="num">− {money(preview!.lineDiscountTotal, currency)}</span>
            </div>
          )}

          <div className="row" style={{ alignItems: 'center', gap: 8 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Discount
              <select
                style={{ width: 70, padding: '3px 4px', fontSize: 12 }}
                value={discountMode}
                onChange={(e) => {
                  setDiscountMode(e.target.value as 'none' | 'pct' | 'amount')
                  setDiscountValue('')
                }}
              >
                <option value="none">none</option>
                <option value="pct">%</option>
                <option value="amount">₹ flat</option>
              </select>
              {discountMode !== 'none' && (
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  style={{ width: 86, padding: '3px 6px', fontSize: 12 }}
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={discountMode === 'pct' ? '10' : '2000'}
                />
              )}
            </span>
            <span className="num">
              {preview?.invoiceDiscountTotal
                ? `− ${money(preview.invoiceDiscountTotal, currency)}`
                : '—'}
            </span>
          </div>
          <div className="row">
            <span>Taxable value</span>
            <span className="num">{money(preview?.taxableValue ?? 0, currency)}</span>
          </div>
          {zeroRated ? (
            <div className="row">
              <span>GST</span>
              <span className="num muted">Nil — zero-rated</span>
            </div>
          ) : isIntra ? (
            <>
              <div className="row">
                <span>CGST</span>
                <span className="num">{money(preview?.cgst ?? 0, currency)}</span>
              </div>
              <div className="row">
                <span>SGST</span>
                <span className="num">{money(preview?.sgst ?? 0, currency)}</span>
              </div>
            </>
          ) : (
            <div className="row">
              <span>IGST</span>
              <span className="num">{money(preview?.igst ?? 0, currency)}</span>
            </div>
          )}
          {Boolean(preview?.roundOff) && (
            <div className="row">
              <span>Round off</span>
              <span className="num">
                {preview!.roundOff > 0 ? '+' : '−'} {money(Math.abs(preview!.roundOff), currency)}
              </span>
            </div>
          )}
          {adjustments.map((a) => (
            <div className="row" key={a.key} style={{ alignItems: 'center', gap: 6 }}>
              <input
                type="text"
                style={{ flex: 1, padding: '3px 6px', fontSize: 12 }}
                placeholder="e.g. AdSense revenue settled"
                value={a.label}
                onChange={(e) =>
                  setAdjustments((xs) =>
                    xs.map((x) => (x.key === a.key ? { ...x, label: e.target.value } : x)),
                  )
                }
              />
              <input
                type="number"
                step="0.01"
                style={{ width: 104, padding: '3px 6px', fontSize: 12 }}
                placeholder="-12314"
                value={a.amount}
                onChange={(e) =>
                  setAdjustments((xs) =>
                    xs.map((x) => (x.key === a.key ? { ...x, amount: e.target.value } : x)),
                  )
                }
              />
              <button
                className="btn ghost sm"
                title="Remove"
                onClick={() => setAdjustments((xs) => xs.filter((x) => x.key !== a.key))}
              >
                ✕
              </button>
            </div>
          ))}

          <div className="row">
            <button
              className="btn ghost sm"
              onClick={() =>
                setAdjustments((xs) => [
                  ...xs,
                  { key: Math.random().toString(36).slice(2), label: '', amount: '' },
                ])
              }
            >
              + Add adjustment (after tax)
            </button>
            <span className="num muted small">
              {preview?.adjustmentTotal
                ? money(preview.adjustmentTotal, currency)
                : ''}
            </span>
          </div>

          <div className="row grand">
            <span>Total</span>
            <span className="num">{money(preview?.total ?? 0, currency)}</span>
          </div>
        </div>
      </Card>

      <div style={{ height: 14 }} />

      <Card title="Notes & terms">
        <div className="grid c2">
          <Field label="Notes shown on the invoice">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
          <Field label="Terms & conditions" hint="Leave blank to use the company default.">
            <textarea value={terms} onChange={(e) => setTerms(e.target.value)} />
          </Field>
        </div>
      </Card>
    </Page>
  )
}
